/**
 * TeamEntryLadder — the roster ladder as a FORM.
 *
 * One row per starting slot then per bench slot, exactly the ladder
 * buildRosterSlots produces for the live draft board, so entering a team here
 * reads like filling in a roster on draft night rather than like a spreadsheet.
 * Visual reference is RosterBoard's card rows: the same `.tag` slot chip in the
 * same POS_COLORS, the name in the middle, the price on the right in accent.
 *
 * Mobile-first single column. The rows wrap rather than scroll sideways,
 * because nearly all of this traffic arrives from a phone.
 *
 * 🔴 AVATARS YES, CLIPS NO. Entry is a form on a phone: a face helps you
 * confirm you picked the right Josh, where a video would be weight with no
 * payoff and would break the one-clip-at-a-time rule the report relies on.
 * The only clip on this surface is the report's hero.
 *
 * 🔴 THE ROW IS SPLIT OUT ON PURPOSE. `LadderRow` takes a slot and arbitrary
 * children, and knows nothing about entry, autocomplete or prices. The league
 * report's expanded picks are slated to become this same ladder in read-only
 * form, so the row must not grow team-entry assumptions. Anything that only
 * makes sense while typing belongs in `EntryRow`, not in `LadderRow`.
 *
 * 🔴 THE LADDER IS FULLY KEYBOARD DRIVABLE (round 6). Sixteen rows is sixteen
 * names and sixteen prices, and reaching for the pointer between every one of
 * them is most of what makes this form feel like a chore. Enter on a name
 * commits the highlighted suggestion and lands on that row's price; Enter on
 * the price lands on the NEXT unfilled row's name; after the last one it lands
 * on the button that grades the team. Type, Enter, type, Enter, all the way
 * down and then straight into the report.
 *
 * TAB IS UNTOUCHED. Enter is the ladder's own verb; Tab stays the browser's,
 * so anybody driving the page by Tab gets exactly what they had before.
 *
 * WHY FOCUS IS QUEUED RATHER THAN CALLED. Committing a player re-renders the
 * row: the price input beside it is `disabled` until the row has a player, so
 * focusing it in the same tick focuses a disabled element and silently does
 * nothing. `go()` tries immediately and leaves the target pending when it
 * cannot land, and the effect below picks it up after the render that made it
 * focusable. The price-to-next-row hop changes no state and therefore never
 * re-renders, which is exactly why the immediate attempt has to exist too.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { POS_COLORS } from "../../data/players";
import PlayerAvatar from "../PlayerAvatar";
import theme from "../../data/theme";
import {
  FLEX_ELIGIBLE,
  SFLEX_ELIGIBLE,
  buildRosterSlots,
  rosterablePositions,
} from "../../utils/rosterSlotFill";
import { ENTRY_POOL } from "../../utils/rateEntryPool";
import { C, SZ } from "../Submit/submitStyles";

const MAX_SUGGESTIONS = 8;

/**
 * Which positions may fill this slot. FLEX and SFLEX read the two exported
 * eligibility lists rather than re-listing them, so a change there reaches the
 * form, the engine and the board together. A bench row accepts anything the
 * league can actually roster.
 */
export function eligibleForSlot(slot, positions) {
  if (slot.pos === "FLEX") return FLEX_ELIGIBLE;
  if (slot.pos === "SFLEX") return SFLEX_ELIGIBLE;
  if (slot.pos === "BN") return [...rosterablePositions(positions)];
  return [slot.pos];
}

/**
 * The ladder rows the caller's state is keyed by. Exported so the page builds
 * its state shape and its entries list from the same source as the render.
 */
export function ladderSlots(positions) {
  return buildRosterSlots(positions || {});
}

/**
 * Ladder state -> the `entries` array rateTeam grades. Rows with no player
 * chosen drop out; a blank price stays null rather than becoming 0, because a
 * free pick and an unknown price are different claims.
 *
 * 🔴 BLANK, ABSENT AND 0 ARE ONE THING: UNPRICED. Nothing in an auction sells
 * for nothing - every room in the corpus opens at $1 - so a typed 0 is somebody
 * clearing the field, not a sale. Normalising it HERE rather than relying on
 * rateTeam's `price > 0` test means the entries array itself carries the honest
 * answer, and any future reader of that array gets it too.
 *
 * 🔴 AND NOTHING HERE MAY EVER INVENT A NUMBER. A grading page that fills in a
 * price nobody typed is worse than one that grades nothing: the figure looks
 * like data, it feeds surplus, and it feeds the value percentile, so one
 * phantom dollar quietly moves a letter grade. unpricedPicks.test.js is the
 * gate on that, end to end.
 */
export function entriesFromLadder(slots, value) {
  const out = [];
  for (const slot of slots) {
    const row = (value || {})[slot.id];
    if (!row || !row.player || !row.pos) continue;
    const raw = row.price === "" || row.price == null ? null : Number(row.price);
    const price = raw === 0 ? null : raw;
    out.push({
      player: row.player,
      pos: row.pos,
      team: row.team || null,
      // Carried so the report can draw a face without re-looking-up the pool.
      sleeperId: row.sleeperId || null,
      slot: slot.label,
      price: Number.isFinite(price) ? price : null,
    });
  }
  return out;
}

/** The slot chip, same shape and palette as the live board's roster rows. */
export function SlotChip({ slot, filledPos }) {
  const key = slot.pos === "BN" || slot.pos === "FLEX" || slot.pos === "SFLEX"
    ? slot.pos
    : filledPos || slot.pos;
  return (
    <span
      className="tag"
      style={{
        background: POS_COLORS[key]?.bg || theme.colors.bg.elevated,
        color: POS_COLORS[key]?.text || theme.colors.text.muted,
        fontSize: SZ.micro,
        padding: "3px 6px",
        minWidth: 42,
        textAlign: "center",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {slot.label}
    </span>
  );
}

/** Chip plus whatever the caller puts beside it. Deliberately knows no more. */
export function LadderRow({ slot, filledPos, children }) {
  return (
    <div
      data-testid={`ladder-row-${slot.label}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 0",
        borderBottom: `1px solid ${C.border.default}`,
      }}
    >
      <SlotChip slot={slot} filledPos={filledPos} />
      {children}
    </div>
  );
}

function PlayerAutocomplete({ slot, positions, taken, row, onPick, onClear, inputRef }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef(null);

  const eligible = useMemo(() => eligibleForSlot(slot, positions), [slot, positions]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    // 🔴 ENTRY_POOL, NOT UNIQUE_PLAYERS. The board is missing 25 players the
    // projection set carries, and a form that cannot accept a roster is a
    // worse failure than any wrong number on the report it produces. See
    // rateEntryPool.js for why the merge is derived rather than listed.
    for (const p of ENTRY_POOL) {
      if (!eligible.includes(p.pos)) continue;
      if (taken.has(`${p.name}-${p.pos}`)) continue;
      if (!p.name.toLowerCase().includes(q)) continue;
      out.push(p);
      if (out.length >= MAX_SUGGESTIONS) break;
    }
    return out;
  }, [query, eligible, taken]);

  function choose(p) {
    onPick({ player: p.name, pos: p.pos, team: p.team, sleeperId: p.sleeper_id || null });
    setQuery("");
    setOpen(false);
    setActive(0);
  }

  if (row && row.player) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
        <PlayerAvatar
          player={{ name: row.player, pos: row.pos, team: row.team, sleeper_id: row.sleeperId }}
          size={24}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: SZ.body,
            fontWeight: 700,
            color: C.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.player}
          {row.team ? (
            <span style={{ fontSize: SZ.micro, color: C.text.faint, fontWeight: 500 }}> {row.team}</span>
          ) : null}
        </span>
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear ${slot.label}`}
          style={{
            border: "none",
            background: "transparent",
            color: C.text.faint,
            cursor: "pointer",
            fontSize: SZ.body,
            lineHeight: 1,
            padding: "2px 6px",
          }}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        // A click on a suggestion fires after blur, so closing is deferred by a
        // tick. Without this the list disappears before the click lands.
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          // Escape is handled ahead of the matches guard: it closes the list
          // and commits NOTHING, which has to hold whether or not the query
          // currently has anything under it.
          if (e.key === "Escape") {
            setOpen(false);
            return;
          }
          if (!matches.length) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            // The highlighted suggestion, and `active` starts at 0, so a bare
            // Enter takes the top match. Nothing to commit means nothing
            // happens: the guard above already returned.
            e.preventDefault();
            choose(matches[active]);
          }
        }}
        placeholder={slot.pos === "BN" ? "Add a bench player" : `Add a ${slot.label}`}
        aria-label={`Player for ${slot.label}`}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          // 16px is load-bearing: anything smaller makes iOS Safari zoom the
          // viewport on focus and the user lands mid-form at 1.5x.
          fontSize: 16,
          borderRadius: theme.radius.lg,
          background: C.bg.deep,
          border: `1px solid ${C.border.default}`,
          color: C.text.primary,
        }}
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 20,
            top: "100%",
            left: 0,
            right: 0,
            margin: "2px 0 0",
            padding: 0,
            listStyle: "none",
            background: C.bg.surface,
            border: `1px solid ${C.border.default}`,
            borderRadius: theme.radius.lg,
            maxHeight: 240,
            overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          }}
        >
          {matches.map((p, i) => (
            <li key={`${p.name}-${p.pos}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseDown={() => {
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                }}
                onClick={() => choose(p)}
                style={{
                  display: "flex",
                  width: "100%",
                  gap: 8,
                  alignItems: "center",
                  textAlign: "left",
                  padding: "8px 10px",
                  border: "none",
                  background: i === active ? C.bg.raised : "transparent",
                  color: C.text.primary,
                  fontSize: SZ.body,
                  cursor: "pointer",
                }}
              >
                <PlayerAvatar player={p} size={24} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </span>
                <span style={{ fontSize: SZ.micro, color: C.text.faint, flexShrink: 0 }}>
                  {p.pos} {p.team}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TeamEntryLadder({ positions, budget, value, onChange, onAdvancePastLast }) {
  const slots = useMemo(() => ladderSlots(positions), [positions]);

  // slot id -> { player, price } DOM nodes. A plain ref map rather than state:
  // nothing about focus belongs in the render, and re-rendering the whole
  // ladder every time a node mounts would be a lot of work for no picture.
  const rowRefs = useRef(new Map());
  const pending = useRef(null);

  function refsFor(id) {
    if (!rowRefs.current.has(id)) rowRefs.current.set(id, {});
    return rowRefs.current.get(id);
  }

  /**
   * The next place a person should be typing after `fromId` is done: the first
   * row below it with nobody in it, else the button that grades the team.
   *
   * Already-filled rows are skipped because a filled row draws a name and a
   * clear button where its input used to be - there is no field there to land
   * in, so walking past it is the only behaviour that can work.
   */
  function nextTarget(fromId) {
    const i = slots.findIndex((s) => s.id === fromId);
    for (let j = i + 1; j < slots.length; j += 1) {
      const row = (value || {})[slots[j].id];
      if (!row || !row.player) return { id: slots[j].id, field: "player" };
    }
    return "submit";
  }

  /** Focus one target now, or report that it is not focusable yet. */
  function focusTarget(target) {
    if (!target) return true;
    if (target === "submit") {
      if (onAdvancePastLast) onAdvancePastLast();
      return true;
    }
    const el = (rowRefs.current.get(target.id) || {})[target.field];
    // A disabled input takes focus from nobody. That is the price field on a
    // row whose player was committed this very tick, and the effect below is
    // what finishes the job once the row re-renders.
    if (!el || el.disabled) return false;
    el.focus();
    return true;
  }

  function go(target) {
    pending.current = focusTarget(target) ? null : target;
  }

  useEffect(() => {
    if (pending.current && focusTarget(pending.current)) pending.current = null;
  });

  // A player already placed cannot be offered again for another slot.
  const taken = useMemo(() => {
    const s = new Set();
    for (const row of Object.values(value || {})) {
      if (row && row.player && row.pos) s.add(`${row.player}-${row.pos}`);
    }
    return s;
  }, [value]);

  const spent = useMemo(
    () =>
      Object.values(value || {}).reduce((a, row) => {
        const n = row && row.price !== "" && row.price != null ? Number(row.price) : 0;
        return a + (Number.isFinite(n) ? n : 0);
      }, 0),
    [value]
  );

  const filledCount = useMemo(
    () => Object.values(value || {}).filter((r) => r && r.player).length,
    [value]
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 6,
          fontSize: SZ.small,
          color: C.text.secondary,
        }}
      >
        <span>
          {filledCount} of {slots.length} slots filled
        </span>
        <span>
          spent{" "}
          <b style={{ color: spent > budget ? C.status.warning : C.text.primary }}>${spent}</b> of ${budget}
        </span>
      </div>

      {slots.map((slot) => {
        const row = (value || {})[slot.id] || null;
        return (
          <LadderRow key={slot.id} slot={slot} filledPos={row && row.pos}>
            <PlayerAutocomplete
              slot={slot}
              positions={positions}
              taken={taken}
              row={row}
              inputRef={(el) => {
                refsFor(slot.id).player = el;
              }}
              onPick={(pick) => {
                onChange(slot.id, { ...pick, price: row ? row.price : "" });
                // Same row, not the next one: a name and its price are one
                // answer, and being thrown to the next player field would make
                // every price a second pass down the ladder.
                go({ id: slot.id, field: "price" });
              }}
              onClear={() => onChange(slot.id, null)}
            />
            <input
              ref={(el) => {
                refsFor(slot.id).price = el;
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                // The price is already committed - it writes on every
                // keystroke - so Enter is purely the hop. An empty field is a
                // legal answer (unpriced is a real state) and advances just the
                // same.
                e.preventDefault();
                go(nextTarget(slot.id));
              }}
              value={row && row.price != null ? row.price : ""}
              onChange={(e) => {
                // Digits only, and no leading zeros: "$" alone and "0" both
                // clear the field, which is what unpriced looks like. There is
                // no $0 sale to type, so the field never holds one.
                const raw = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
                onChange(slot.id, { ...(row || {}), price: raw });
              }}
              disabled={!row || !row.player}
              inputMode="numeric"
              aria-label={`Price for ${slot.label}`}
              placeholder="$"
              style={{
                width: 58,
                flexShrink: 0,
                boxSizing: "border-box",
                padding: "8px 6px",
                fontSize: 16,
                textAlign: "right",
                borderRadius: theme.radius.lg,
                background: C.bg.deep,
                border: `1px solid ${C.border.default}`,
                color: C.brand.accent,
                fontWeight: 700,
                opacity: row && row.player ? 1 : 0.4,
              }}
            />
          </LadderRow>
        );
      })}

      <div style={{ marginTop: 12, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
        Prices are optional. Leave them blank and you still get projected points and positional
        strength; fill them all in and you also get a market grade. Kicker and defense count toward
        your projected points, under standard K and DEF scoring, but their prices never affect the
        grade.
      </div>
    </div>
  );
}
